import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Alert } from "../../components/ui/Alert";
import { FormSkeleton } from "../../components/ui/FormSkeleton";
import { useApi } from "../../context/ApiContext";
import { PageBuilder } from "../../components/pageBuilder/PageBuilder";
import { PageBuilderProvider, usePageBuilder } from "../../context/PageBuilderContext";
import { IComponentNode } from "../../types/pageBuilder.types";
import { DndContext, DragEndEvent, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { arrayMove, SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { FaEdit, FaGripVertical, FaArrowUp, FaArrowDown, FaTimes } from "react-icons/fa";

export const PageForm: React.FC = () => {
 const { t } = useTranslation();
 const { pageId } = useParams<{ pageId: string }>();
 const navigate = useNavigate();
 const { api } = useApi();
 const [initialLoading, setInitialLoading] = useState(!!(pageId && pageId !== "new"));
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [showPageBuilder, setShowPageBuilder] = useState(false);
 const [formData, setFormData] = useState({
  title: "",
  slug: "",
  type: "static" as "static",
  metaTitle: "",
  metaDescription: "",
  isActive: false, // Default to false for new pages
  pageBuilderLayout: null as IComponentNode | null,
 });
 const [hasZones, setHasZones] = useState(false);
 const [zones, setZones] = useState<Record<string, IComponentNode>>({});
 const [zoneNames, setZoneNames] = useState<string[]>([]);
 const [editingZone, setEditingZone] = useState<{ name: string; content: IComponentNode } | null>(null);

 useEffect(() => {
  if (pageId && pageId !== "new") {
   fetchPage();
  }
 }, [pageId]);
 
 // No longer need to handle action from URL params - buttons are in the form

 const fetchPage = async () => {
  try {
   setInitialLoading(true);
   setError(null);
   
   // Try fetching from page builder endpoint first (includes system pages)
   let page: any = null;
   try {
    const pbResponse = await api.get(`/page-builder/pages/${pageId}`);
    page = pbResponse.data.page;
   } catch (err: any) {
    // If not found in page builder, try CMS pages endpoint
    if (err.response?.status === 404) {
     const cmsResponse = await api.get(`/pages`);
     const pages = cmsResponse.data.pages || [];
     page = pages.find((p: any) => p._id === pageId);
    } else {
     throw err;
    }
   }
   
   if (page) {
    const hasPageBuilderLayout = page.layout && typeof page.layout === "object";
    
    // Check if page has zones (from unified model)
    const pageZones = page.zones || {};
    const hasPageZones = Object.keys(pageZones).length > 0;
    setHasZones(hasPageZones);
    
    // Set zones and zone names
    setZones(pageZones);
    setZoneNames(Object.keys(pageZones));
    
    setFormData({
     title: page.title || "",
     slug: page.slug || "",
     type: page.type || page.pageType || "static",
     metaTitle: page.metaTitle || "",
     metaDescription: page.metaDescription || "",
     isActive: page.isActive !== undefined ? page.isActive : true,
     // For static pages, allow both layout and zones
     // For policy pages, show page builder
     // For system pages, only zones
     pageBuilderLayout: hasPageBuilderLayout ? page.layout : null,
    });
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pages.errors.fetch"));
  } finally {
   setInitialLoading(false);
  }
 };


 const handleTitleChange = (title: string) => {
  setFormData({
   ...formData,
   title,
   // Slug will be auto-generated on the backend from title
  });
 };

 
 const handleEditZone = (zoneName: string) => {
  const zoneContent = zones[zoneName] || {
   id: `zone-${zoneName}`,
   type: "container",
   props: {},
   styles: { default: {} },
   children: [],
  };
  setEditingZone({ name: zoneName, content: zoneContent });
 };
 
 const handleSaveZone = async (zoneName: string, layout: IComponentNode) => {
  if (!pageId || pageId === "new") {
   // If page doesn't exist yet, just update local state
   const updatedZones = { ...zones, [zoneName]: layout };
   setZones(updatedZones);
   setZoneNames(Object.keys(updatedZones));
   setEditingZone(null);
   return;
  }

  // Save zone directly to database
  try {
   const updatedZones = { ...zones, [zoneName]: layout };
   await api.put(`/page-builder/pages/${pageId}`, {
    zones: updatedZones,
   });
   setZones(updatedZones);
   setEditingZone(null);
  } catch (error: any) {
   console.error("Error saving zone:", error);
   setError(error.response?.data?.error || t("admin.pageZones.errors.saveZone"));
  }
 };
 
 const handleCancelZoneEdit = () => {
  setEditingZone(null);
 };
 
 const handleZoneOrderChange = (event: DragEndEvent) => {
  const { active, over } = event;
  
  if (!over || active.id === over.id) return;
  
  const oldIndex = zoneNames.indexOf(active.id as string);
  const newIndex = zoneNames.indexOf(over.id as string);
  
  if (oldIndex !== -1 && newIndex !== -1) {
   const newZoneNames = arrayMove(zoneNames, oldIndex, newIndex);
   setZoneNames(newZoneNames);
   
   // Reorder zones object to match new order
   const reorderedZones: Record<string, IComponentNode> = {};
   newZoneNames.forEach((zoneName) => {
    if (zones[zoneName]) {
     reorderedZones[zoneName] = zones[zoneName];
    }
   });
   setZones(reorderedZones);
  }
 };
 
 const handleMoveZone = (zoneName: string, direction: "up" | "down") => {
  const currentIndex = zoneNames.indexOf(zoneName);
  if (currentIndex === -1) return;
  
  const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
  if (newIndex < 0 || newIndex >= zoneNames.length) return;
  
  const newZoneNames = arrayMove(zoneNames, currentIndex, newIndex);
  setZoneNames(newZoneNames);
  
  // Reorder zones object to match new order
  const reorderedZones: Record<string, IComponentNode> = {};
  newZoneNames.forEach((name) => {
   if (zones[name]) {
    reorderedZones[name] = zones[name];
   }
  });
  setZones(reorderedZones);
 };
 

 // Reset zone handler - will be called by PageBuilder component's reset button
 const handleResetZone = async (zoneName: string, defaultTemplate: IComponentNode) => {
  if (!pageId || pageId === "new") {
   setError(t("admin.pageForm.savePageFirst") || "Please save the page first before resetting zones");
   return;
  }

  try {
   setError(null);
   // Update the zone with default template
   const updatedZones = { ...zones, [zoneName]: defaultTemplate };
   await api.put(`/page-builder/pages/${pageId}`, {
    zones: updatedZones,
   });

   setZones(updatedZones);
   // Update the editing zone content so PageBuilder shows the reset content
   setEditingZone({ name: zoneName, content: defaultTemplate });
  } catch (error: any) {
   console.error("Error resetting zone:", error);
   setError(error.response?.data?.error || t("admin.pageForm.errors.resetZone") || "Failed to reset zone");
  }
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  // Don't allow form submission when page builder is open
  if (showPageBuilder) {
   return;
  }
  try {
   setSaving(true);
   setError(null);

   // Allow saving without content/layout for new pages
   // Content can be added after the page is created

   const submitData: any = {
    title: formData.title,
    // Don't send slug when creating - backend will auto-generate from title
    // Only send slug when updating existing page
    ...(pageId && pageId !== "new" ? { slug: formData.slug } : {}),
    type: formData.type,
    metaTitle: formData.metaTitle,
    metaDescription: formData.metaDescription,
    isActive: formData.isActive,
    // Include layout only if it exists (allow saving without content for new pages)
    // Include zones if page has zones
    ...(hasZones ? { zones } : (formData.pageBuilderLayout ? { layout: formData.pageBuilderLayout } : {})),
   };

   // Save to unified PageModel
   // For pages with zones, use page-builder endpoint
   // For pages without zones, use pages endpoint
   if (hasZones) {
    if (pageId && pageId !== "new") {
     await api.put(`/page-builder/pages/${pageId}`, submitData);
    } else {
     await api.post("/page-builder/pages", submitData);
    }
   } else {
    if (pageId && pageId !== "new") {
     await api.put(`/pages/${pageId}`, submitData);
     navigate("/admin/pages");
    } else {
     const response = await api.post("/pages", submitData);
     // Navigate to edit form after creation
     if (response.data && response.data._id) {
      navigate(`/admin/pages/${response.data._id}`);
     } else {
      navigate("/admin/pages");
     }
    }
   }
  } catch (err: any) {
   const errorMessage = err.response?.data?.error || err.response?.data?.message || t("admin.pages.errors.save");
   setError(errorMessage);
   console.error("Error saving page:", err.response?.data || err);
  } finally {
   setSaving(false);
  }
 };

 const handlePageBuilderCancel = () => {
  setShowPageBuilder(false);
 };

 // Auto-save layout changes to form state
 const handleLayoutChange = useCallback((layout: IComponentNode | null) => {
  setFormData((prevFormData) => ({
   ...prevFormData,
   pageBuilderLayout: layout,
  }));
 }, []);

 // Save layout to form state and database when save button is clicked in preview mode
 const handleSaveToForm = async (layout: IComponentNode) => {
  // Update form state first
  setFormData({
   ...formData,
   pageBuilderLayout: layout,
  });
  
  // Also save directly to database if editing existing page
  if (pageId && pageId !== "new") {
   try {
    setSaving(true);
    setError(null);

    const submitData: any = {
     title: formData.title,
     slug: formData.slug,
     type: formData.type,
     metaTitle: formData.metaTitle,
     metaDescription: formData.metaDescription,
     isActive: formData.isActive,
     layout: layout,
    };

    await api.put(`/pages/${pageId}`, submitData);
    setError(null);
    // Show success feedback
   } catch (err: any) {
    const errorMessage = err.response?.data?.error || err.response?.data?.message || t("admin.pages.errors.save");
    setError(errorMessage);
    console.error("Error saving page:", err.response?.data || err);
   } finally {
    setSaving(false);
   }
  }
 };

 if (initialLoading) {
  return (
   <Container>
    <Stack spacing="lg">
     <div className="flex items-center justify-between">
      <Heading size="xl">
       {pageId && pageId !== "new" ? t("admin.pageForm.editPage") : t("admin.pageForm.newPage")}
      </Heading>
      <Button variant="outline" onClick={() => navigate("/admin/pages")}>
       {t("admin.pageForm.backToPages")}
      </Button>
     </div>
     <Card padding="lg">
      <FormSkeleton fields={6} />
     </Card>
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">
      {pageId && pageId !== "new" ? t("admin.pageForm.editPage") : t("admin.pageForm.newPage")}
     </Heading>
     <Button variant="outline" onClick={() => navigate("/admin/pages")}>
      Back to Pages
     </Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    <Card padding="lg">
     <form 
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
       // Prevent form submission when PageBuilder is open, but allow input in editable areas
       if (showPageBuilder && (e.key === "Enter" || e.key === " ")) {
        const target = e.target as HTMLElement;
        // Check if the event target is in an editable area
        const isEditableContent = target.closest(".ql-editor") ||
                                  target.closest('[contenteditable="true"]') ||
                                  target.closest("input") ||
                                  target.closest("textarea") ||
                                  target.closest("select") ||
                                  target.tagName === "INPUT" ||
                                  target.tagName === "TEXTAREA" ||
                                  target.tagName === "SELECT" ||
                                  target.isContentEditable;
        // Only prevent default if NOT in an editable area
        if (!isEditableContent) {
         e.preventDefault();
         e.stopPropagation();
        }
       }
      }}
     >
      <Stack spacing="md">
       <Input
        label={t("admin.pageForm.title")}
        required
        value={formData.title}
        onChange={(e) => handleTitleChange(e.target.value)}
       />



       {/* Zone Management - Show for all pages that have zones (system pages, static pages with zones) */}
       {pageId && pageId !== "new" && hasZones && (
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.pageForm.zones")}
          {zoneNames.length > 0 && (
           <span className="ml-2 text-xs font-normal text-gray-500">
            ({t("admin.pageForm.dragToReorder")})
           </span>
          )}
         </label>
         <div className="border border-gray-200 p-4 rounded">
          {zoneNames.length === 0 ? (
           <p className="text-sm text-gray-500 text-center py-4">
            {t("admin.pageForm.noZonesAvailable")}
           </p>
          ) : (
         <ZoneList
          zoneNames={zoneNames}
          onEditZone={handleEditZone}
          onMoveZone={handleMoveZone}
          onOrderChange={handleZoneOrderChange}
         />
          )}
         </div>
        </div>
       )}

       {/* Page Builder - Show for pages without zones (static pages) */}
       {!hasZones && (
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.pageForm.content")}
         </label>
         {pageId && pageId !== "new" ? (
          <>
           {!showPageBuilder && (
            <div className="flex gap-2 mb-2">
             <Button
              type="button"
              variant="secondary"
              onClick={() => setShowPageBuilder(true)}
             >
              {formData.pageBuilderLayout ? t("admin.pageForm.editPageBuilder") : t("admin.pageForm.openPageBuilder")}
             </Button>
             {formData.pageBuilderLayout && !showPageBuilder && (
              <span className="flex items-center text-sm text-green-600">
               ✓ {t("admin.pageForm.layoutConfigured")}
              </span>
             )}
            </div>
           )}
           {showPageBuilder && (
            <PageBuilderProvider initialLayout={formData.pageBuilderLayout}>
             <PageBuilderWrapper
              initialLayout={formData.pageBuilderLayout}
              onLayoutChange={handleLayoutChange}
              onCancel={handlePageBuilderCancel}
              onSaveToForm={handleSaveToForm}
              pageSlug={formData.slug}
              pageType="static"
             />
            </PageBuilderProvider>
           )}
           {!showPageBuilder && !formData.pageBuilderLayout && (
            <div className="border-2 border-dashed border-gray-300 p-8 text-center">
             <p className="text-gray-500 mb-2">{t("admin.pageForm.noContentConfigured")}</p>
             <p className="text-sm text-gray-400">{t("admin.pageForm.clickToStartBuilding")}</p>
            </div>
           )}
          </>
         ) : (
          <div className="border-2 border-dashed border-gray-300 p-8 text-center">
           <p className="text-gray-500 mb-2">{t("admin.pageForm.savePageFirst")}</p>
           <p className="text-sm text-gray-400">{t("admin.pageForm.savePageToBuildContent")}</p>
          </div>
         )}
        </div>
       )}

       <Input
        label={t("admin.pageForm.metaTitle")}
        value={formData.metaTitle}
        onChange={(e) =>
         setFormData({ ...formData, metaTitle: e.target.value })
        }
        placeholder={t("admin.pageForm.metaTitlePlaceholder")}
       />

       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.pageForm.metaDescription")}
        </label>
        <textarea
         value={formData.metaDescription}
         onChange={(e) =>
          setFormData({ ...formData, metaDescription: e.target.value })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
         rows={3}
         placeholder={t("admin.pageForm.metaDescriptionPlaceholder")}
        />
       </div>

       {/* Only show active checkbox when editing existing page */}
       {pageId && pageId !== "new" && (
        <div>
         <label className="flex items-center gap-2">
          <input
           type="checkbox"
           checked={formData.isActive}
           onChange={(e) =>
            setFormData({ ...formData, isActive: e.target.checked })
           }
           className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">{t("admin.active")}</span>
         </label>
        </div>
       )}

       <div className="flex gap-2">
        <Button type="submit" loading={saving}>
         {pageId && pageId !== "new" ? t("common.update") : t("common.create")}
        </Button>
        <Button
         type="button"
         variant="outline"
         onClick={() => navigate("/admin/pages")}
        >
         {t("common.cancel")}
        </Button>
       </div>
      </Stack>
     </form>
    </Card>
   </Stack>
   
   {/* Zone Editor Modal */}
   {editingZone && (
    <PageBuilderProvider initialLayout={editingZone.content}>
     <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
       <h2 className="text-lg font-semibold">
        {t("admin.pageForm.editZone", { zoneName: editingZone.name })}
       </h2>
       <button
        onClick={handleCancelZoneEdit}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
       >
        <FaTimes />
        Cancel
       </button>
      </div>
      <div className="flex-1 overflow-hidden">
       <PageBuilder
        initialLayout={editingZone.content}
        onCancel={handleCancelZoneEdit}
        onSave={(layout) => handleSaveZone(editingZone.name, layout)}
        zoneName={editingZone.name}
        pageName={formData.slug || pageId || undefined}
        pageType="static"
        onReset={(defaultTemplate) => handleResetZone(editingZone.name, defaultTemplate)}
       />
      </div>
     </div>
    </PageBuilderProvider>
   )}
  </Container>
 );
};

// Zone List Component with Drag and Drop
interface ZoneListProps {
 zoneNames: string[];
 onEditZone: (zoneName: string) => void;
 onMoveZone: (zoneName: string, direction: "up" | "down") => void;
 onOrderChange: (event: DragEndEvent) => void;
}

const ZoneList: React.FC<ZoneListProps> = ({ 
 zoneNames, 
 onEditZone, 
 onMoveZone,
 onOrderChange 
}) => {
 const sensors = useSensors(
  useSensor(PointerSensor),
  useSensor(KeyboardSensor, {
   coordinateGetter: sortableKeyboardCoordinates,
  })
 );

 return (
  <DndContext
   sensors={sensors}
   collisionDetection={closestCenter}
   onDragEnd={onOrderChange}
  >
   <SortableContext items={zoneNames} strategy={verticalListSortingStrategy}>
    <div className="space-y-2">
     {zoneNames.map((zoneName, index) => (
      <SortableZoneItem
       key={zoneName}
       zoneName={zoneName}
       onEdit={onEditZone}
       onMove={onMoveZone}
       isFirst={index === 0}
       isLast={index === zoneNames.length - 1}
      />
     ))}
    </div>
   </SortableContext>
  </DndContext>
 );
};

interface SortableZoneItemProps {
 zoneName: string;
 onEdit: (zoneName: string) => void;
 onMove: (zoneName: string, direction: "up" | "down") => void;
 isFirst: boolean;
 isLast: boolean;
}

const SortableZoneItem: React.FC<SortableZoneItemProps> = ({
 zoneName, 
 onEdit, 
 onMove,
 isFirst,
 isLast
}) => {
 const { t } = useTranslation();
 const {
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  isDragging,
 } = useSortable({ id: zoneName });

 const style = {
  transform: CSS.Transform.toString(transform),
  transition,
  opacity: isDragging ? 0.5 : 1,
 };

 return (
  <div
   ref={setNodeRef}
   style={style}
   className={`flex items-center gap-3 p-3 bg-gray-50 border-2 ${
    isDragging ? "border-blue-500" : "border-transparent"
   } hover:bg-gray-100 transition-colors`}
  >
   <div
    {...attributes}
    {...listeners}
    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
    title={t("admin.pageForm.dragToReorder")}
   >
    <FaGripVertical />
   </div>
   <span className="flex-1 text-sm font-medium text-gray-700 capitalize">
    {zoneName.replace(/-/g, " ")}
   </span>
   <div className="flex items-center gap-1">
    <button
     type="button"
     onClick={() => onMove(zoneName, "up")}
     disabled={isFirst}
     className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
     title={t("admin.pageForm.moveUp")}
    >
     <FaArrowUp />
    </button>
    <button
     type="button"
     onClick={() => onMove(zoneName, "down")}
     disabled={isLast}
     className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
     title={t("admin.pageForm.moveDown")}
    >
     <FaArrowDown />
    </button>
    <button
     type="button"
     onClick={() => onEdit(zoneName)}
     className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
    >
     <FaEdit />
     {t("common.edit")}
    </button>
   </div>
  </div>
 );
};

// Wrapper component to sync layout changes with form state
const PageBuilderWrapper: React.FC<{
 initialLayout: IComponentNode | null;
 onLayoutChange: (layout: IComponentNode | null) => void;
 onCancel: () => void;
 onSaveToForm: (layout: IComponentNode) => void;
 pageSlug?: string;
 pageType?: string;
}> = ({ initialLayout, onLayoutChange, onCancel, onSaveToForm, pageSlug, pageType }) => {
 const { layout } = usePageBuilder();

 React.useEffect(() => {
  if (layout) {
   onLayoutChange(layout);
  }
 }, [layout, onLayoutChange]);

 const handleSave = (savedLayout: IComponentNode) => {
  // Update form state with saved layout
  onSaveToForm(savedLayout);
 };

 return (
  <PageBuilder
   initialLayout={initialLayout}
   onCancel={onCancel}
   onSave={handleSave}
   pageName={pageSlug}
   pageType={pageType}
  />
 );
};

export default PageForm;

